import type {
  PublicConfig,
  RecordedAnswer,
  SubmissionRequest,
  SubmissionResponse
} from "../types";

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const contentType = response.headers.get("Content-Type") ?? "";

    if (contentType.includes("application/json")) {
      const payload = (await response.json()) as { error?: string };
      const errorMsg = payload.error || `Request failed with ${response.status}`;
      // Guard against HTML error text being serialised inside the JSON error field
      // (e.g. Power Automate returning a Microsoft error page that bubbles through)
      const isHtmlPayload =
        errorMsg.trimStart().toLowerCase().startsWith("<!doctype") ||
        errorMsg.trimStart().startsWith("<html");
      throw new Error(
        isHtmlPayload
          ? `Server error (${response.status}). Please try again or contact support if the problem persists.`
          : errorMsg
      );
    }

    const text = await response.text();
    const isHtml =
      contentType.includes("text/html") ||
      text.trimStart().toLowerCase().startsWith("<!doctype") ||
      text.trimStart().startsWith("<html");
    throw new Error(
      isHtml
        ? `Server error (${response.status}). Please try again or contact support if the problem persists.`
        : text || `Request failed with status ${response.status}`
    );
  }

  return (await response.json()) as T;
}

export async function fetchPublicConfig(invitationToken: string): Promise<PublicConfig> {
  const response = await fetch(`/api/config?invite=${encodeURIComponent(invitationToken)}`);
  return parseJsonResponse<PublicConfig>(response);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const result = reader.result as string;
      // Data URL format: "data:<mime>;base64,<data>"
      // The MIME type may contain commas (e.g. "video/webm;codecs=vp9,opus"), so
      // splitting on "," is unreliable. Find the ";base64," marker explicitly instead.
      const marker = ";base64,";
      const markerIndex = result.indexOf(marker);
      if (markerIndex === -1) {
        reject(new Error("Failed to encode recording for upload."));
        return;
      }
      resolve(result.slice(markerIndex + marker.length));
    };
    reader.onerror = () => reject(new Error("Failed to read recording file."));
  });
}

export async function submitApplication(
  payload: SubmissionRequest,
  answers: RecordedAnswer[]
): Promise<SubmissionResponse> {
  // Encode video blobs to base64 here in the browser rather than in the Cloudflare Worker.
  // Workers have a strict CPU time limit; base64-encoding large videos inside the Worker
  // kills it before it can respond (Error 1102 → 503). Browsers have no such limit.
  const answersByQuestionId = new Map(answers.map((a) => [a.questionId, a]));
  const encodedAnswers = await Promise.all(
    payload.answers.map(async (meta) => {
      const recorded = answersByQuestionId.get(meta.questionId);
      if (!recorded) {
        throw new Error(`Missing recording for question ${meta.questionId}.`);
      }
      return { ...meta, contentBase64: await blobToBase64(recorded.blob) };
    })
  );

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 5 * 60 * 1000);

  try {
    const response = await fetch("/api/submissions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        invitationToken: payload.invitationToken,
        applicant: payload.applicant,
        questionSetId: payload.questionSetId,
        turnstileToken: payload.turnstileToken,
        answers: encodedAnswers
      }),
      signal: controller.signal
    });
    return parseJsonResponse<SubmissionResponse>(response);
  } catch (fetchError) {
    if (fetchError instanceof DOMException && fetchError.name === "AbortError") {
      throw new Error(
        "Upload timed out. Please check your connection and try submitting again."
      );
    }
    throw fetchError;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function buildSubmissionRequest(params: {
  invitationToken: string;
  applicant: SubmissionRequest["applicant"];
  questionSetId: string;
  turnstileToken: string;
  answers: RecordedAnswer[];
}): SubmissionRequest {
  return {
    invitationToken: params.invitationToken,
    applicant: params.applicant,
    questionSetId: params.questionSetId,
    turnstileToken: params.turnstileToken,
    answers: params.answers.map((answer) => ({
      questionId: answer.questionId,
      prompt: answer.prompt,
      fileName: answer.fileName,
      mimeType: answer.mimeType,
      size: answer.blob.size,
      durationSeconds: answer.durationSeconds
    }))
  };
}
